import type { Session } from "../api/models";
import { FloorMap } from "../floor/FloorMap";
import type { Point } from "../floor/floor-logic";
import { SessionMoney } from "../in-play/SessionCard";
import { SELECTED_LOCATION_SX } from "../in-play/SessionSidebars";
import { PButton } from "../ui/PButton";
import { PSidebar } from "../ui/PSidebar";
import { PTag } from "../ui/PWidgets";
import { age, genderSeverity, mediumDate, titleCase, upper, type CurrentPatron } from "./assist-logic";
import { BareSidebar, Pi } from "./widgets";

// THE PATRON DETAIL AND PATRON LOCATION `p-sidebar`S OF `pillars-assist.component.html`.

function HostAndGender({ patron, className }: { patron: CurrentPatron | null; className: string }) {
  return (
    <div className={className}>
      <div className="text-sm text-500 mt-1">Host: {patron?.hostName || "n/a"}</div>
      <div>
        <PTag value={upper(patron?.gender)} severity={genderSeverity(patron?.gender)} rounded className="p-component" />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="col-12">
      <div className="field">
        <label className="font-semibold text-900">{label}</label>
        {children}
      </div>
    </div>
  );
}

function Card({ header, children }: { header: string; children: React.ReactNode }) {
  return (
    <div className="p-card p-component mb-3">
      <div className="p-card-body">
        <div className="p-card-title">{header}</div>
        <div className="p-card-content">{children}</div>
      </div>
    </div>
  );
}

// PATRON DETAIL: NON-MODAL, RIGHT, `w-full md:w-30rem`. THE NAME WITH A LINK TO THE PROFILE, HOST
// AND GENDER, THEN "PERSONAL INFORMATION" AND "CONTACT INFORMATION" CARDS
export function PatronDetailSidebar({ open, patron, onClose, onProfile }: { open: boolean; patron: CurrentPatron | null; onClose: () => void; onProfile: () => void }) {
  const p = patron;
  return (
    <PSidebar open={open} onClose={onClose} position="right" modal={false} width={{ xs: "100%", md: "30rem" }} ariaLabel="Patron detail">
      <div className="pillars-assist-inherit">
        <div className="p-3">
          <div className="flex flex-row align-items-center justify-content-between">
            <div>
              <div className="text-2xl font-bold text-primary">{upper(p?.name)}</div>
            </div>
            <div>
              <PButton icon="external-link" rounded variant="text" hostElement ariaLabel="Open player profile" disabled={!p} onClick={onProfile} />
            </div>
          </div>
          <HostAndGender patron={p} className="flex flex-row align-items-center justify-content-between mt-3 mb-4" />

          <Card header="Personal Information">
            <div className="grid">
              <Field label="Full Name">
                <div className="m-0">
                  {titleCase(p?.firstName)} {titleCase(p?.middleName)} {titleCase(p?.lastName)}
                </div>
              </Field>
              <Field label="Player Level">
                <div className="m-0">{p?.Player?.PlayerCard?.PlayerCardLevel?.description || "Not Set"}</div>
              </Field>
              <Field label="Card ID">
                <div className="m-0">{upper(p?.cardId)}</div>
              </Field>
              <Field label="Birth Date">
                <div className="flex flex-column">
                  <span>{mediumDate(p?.birthdate)}</span>
                  <small className="text-500">({age(p?.birthdate)} years old)</small>
                </div>
              </Field>
              <Field label="Enrollment Date">
                <div className="m-0">{mediumDate(p?.enrollDate)}</div>
              </Field>
              <Field label="Floor Location">
                <div className="m-0">{p?.location || "No Active Session"}</div>
              </Field>
            </div>
          </Card>

          <Card header="Contact Information">
            <div className="grid">
              <Field label="Email">
                <div className="flex align-items-center gap-2">
                  <Pi name="envelope" className="text-500" />
                  <span>{p?.email}</span>
                </div>
              </Field>
              <Field label="Mobile Phone">
                <div className="flex align-items-center gap-2">
                  <Pi name="phone" className="text-500" />
                  <span>{p?.mobile}</span>
                </div>
              </Field>
              <Field label="Address">
                <div className="flex align-items-start gap-2">
                  <Pi name="map-marker" className="text-500 mt-1" />
                  <div>
                    <div className="m-0">{p?.address}</div>
                    <div className="m-0">
                      {titleCase(p?.city)}, {upper(p?.state)} {p?.zip}
                    </div>
                  </div>
                </div>
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </PSidebar>
  );
}

// PATRON LOCATION: NON-MODAL, RIGHT, FULL WIDTH, NO CLOSE ICON, NOT DISMISSIBLE. THE FLOOR MAP,
// THEN (ONCE THE SESSION LOADS) THE ORANGE LOCATION WITH ZOOM IN / ZOOM OUT, THE SESSION CARD,
// AND "CANCEL"
export function PatronLocationSidebar(props: {
  open: boolean;
  patron: CurrentPatron | null;
  session: Session | null;
  zoomedIn: boolean;
  pulse: Point | null;
  zoomTo: Point | null;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onCancel: () => void;
}) {
  const { open, patron, session: s, zoomedIn, pulse, zoomTo, onZoomIn, onZoomOut, onCancel } = props;
  return (
    <BareSidebar open={open} position="right" width="100%" ariaLabel="Patron location">
      <div className="pillars-assist-inherit">
        <div className="w-full parent p-4">
          <div className="child w-full">
            <FloorMap svgId="paMap" pulse={pulse} zoomTo={zoomTo} />
          </div>
          {s && (
            <div className="mt-3 w-full">
              <div className="flex flex-row align-items-center justify-content-between">
                <div className="flex flex-row align-items-center font-bold text-lg color-orange-600">
                  <Pi name="map-marker" size="1.5rem" />
                  <div className="ml-2">{s.location}</div>
                </div>
                <div>
                  {zoomedIn ? (
                    <PButton label="Zoom Out" icon={<Pi name="search-minus" />} hostElement className="font-bold min-width-125" onClick={onZoomOut} />
                  ) : (
                    <PButton label="Zoom In" icon="search" hostElement className="font-bold min-width-125" onClick={onZoomIn} />
                  )}
                </div>
              </div>
              <div className="mt-4 p-4 border-1 surface-border surface-card border-round flex flex-column">
                <HostAndGender patron={patron} className="flex flex-row align-items-center justify-content-between" />
                <div className="mt-3">
                  <div className="flex flex-row align-items-center">
                    {s.cmsPlayerId ? <span className="material-symbols-rounded color-primary">credit_card</span> : <span className="material-symbols-rounded">credit_card_off</span>}
                    <div className="flex flex-row align-items-center justify-content-between w-full">
                      <div className="text-overflow-ellipsis font-bold ml-1">{upper(s.playerName)}</div>
                      {patron?.birthdate && (
                        <div className="mt-1 text-sm uppercase font-bold">
                          <div>{mediumDate(patron.birthdate)}</div>
                          <div>{age(patron.birthdate)} years old</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 w-full flex flex-row align-items-center justify-content-between gap-2">
                    <div className="text-overflow-ellipsis text-sm">{s.description}</div>
                  </div>
                  <div className="mt-3 flex flex-row align-items-center justify-content-between">
                    <SessionMoney session={s} carded={!!s.cmsPlayerId} />
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <PButton label="Cancel" severity="warning" hostElement className="w-full bg-selected-location pillars-assist-selected-location" sx={{ ...SELECTED_LOCATION_SX, width: "100%" }} onClick={onCancel} />
              </div>
            </div>
          )}
        </div>
      </div>
    </BareSidebar>
  );
}
